import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Stepper from '@material-ui/core/Stepper';
import withStyles from '@material-ui/core/styles/withStyles';
import React, { useState, useMemo, useCallback } from 'react';
import { useRefresh, useNotify } from 'react-admin';
import FundInvestorApi from '../../core/apis/FundInvestorApi';

import DialogTitleWithCloseButton from '../../core/components/DialogTitleWithCloseButton';
import Confirm from '../../core/components/forked/layout/Confirm';
import ManageStep from './contactssteps/ManageStep.js';
import ContactsStep from './contactssteps/ContactsStep.js';
import { ManageContactContext } from './contactssteps/ManageContactContext';
import { StepConnector } from '@material-ui/core';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';

const styles = () => ({
  stepper: {
    backgroundColor: '#FFFFFF',
    padding: '0 0 0px',
    display: 'inline-flex'
  },
  leftWrapper: {
    flexGrow: 1
  },
  button: {
    '& span': {
      padding: 0
    }
  },
  backButton: {
    border: '1px solid #E0E0E0',
    '& span': {
      padding: 0
    }
  },
  saveButton: {
    border: '1px solid #E0E0E0',
    '& span': {
      padding: 0
    }
  },
  root: {
    paddingTop: '10px',
    paddingBottom: '10px'
  },
  paperWidthFalse: {
    height: 'inherit',
    width: '100%'
  },
  dialogContent: {
    padding: '0px',
    display: 'flex',
    flexDirection: 'column'
  },
  stepperContainer: {
    backgroundColor: '#FFFFFF',
    padding: '11px 8px',
    display: 'flex',
    borderBottom: '1px solid #E0E0E0'
  },
  stepContainer: {
    padding: '16px',
    display: 'flex',
    '& .simple-form': {
      display: 'flex',
      maxHeight: '100%',
      width: '100%',
      '& > div': {
        width: '100%'
      }
    }
  }
});

const FundInvestorStepper = props => {
  const [activeStep, setActiveStep] = useState(0);
  const { ids = [] } = props;
  const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState(false);
  const [contactsContext, setContactsContext] = useState({});
  const providerContactsContext = useMemo(
    () => ({ contactsContext, setContactsContext }),
    [contactsContext, setContactsContext]
  );
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();

  const refresh = useRefresh();
  const { classes, operation, showDialog, handleDialogToggle, ...rest } = props;
  // List of steps
  const steps = useMemo(
    () => [
      {
        name: 'fundinvestors',
        label: props.fundId ? 'Select Investors' : 'Select Funds',
        component: ManageStep,
        nextButton: ids.length > 0,
        backButton: false,
        shouldDisplay: true
      },
      {
        name: 'contacts',
        label: 'Manage Contacts',
        component: ContactsStep,
        nextButton: false,
        backButton: true,
        shouldDisplay: ids.length > 0
      }
    ],
    [props.fundId, ids.length]
  );

  const hasNext = steps[activeStep] ? steps[activeStep].nextButton : false;
  const hasBack = steps[activeStep] ? steps[activeStep].backButton : false;

  const handleCloseClick = useCallback(() => {
    setConfirmDialogIsOpen(true);
  }, []);

  const handleConfirmDialogClose = useCallback(() => {
    setConfirmDialogIsOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    setConfirmDialogIsOpen(false);
    handleDialogToggle();
    refresh();
  }, [handleDialogToggle, refresh]);

  const onSubmitEnd = useCallback(
    async values => {
      if (ids.length === 0) {
        const fundinvestors = [];
        if (props.fundId) {
          values.investors.forEach(investorId => {
            fundinvestors.push({
              fundId: props.fundId,
              investorId
            });
          });
        } else {
          values.funds.forEach(fundId => {
            fundinvestors.push({
              fundId,
              investorId: props.investorId
            });
          });
        }
        try {
          await FundInvestorApi.create(fundinvestors);
          notify('ra.notification.created');
          handleClose();
        } catch (e) {
          notifyServerError(e);
        }
      } else if (activeStep === 1) {
        handleClose();
      } else {
        setActiveStep(activeStep + 1);
      }
    },
    [
      activeStep,
      handleClose,
      ids.length,
      notify,
      props.fundId,
      props.investorId,
      notifyServerError
    ]
  );

  const getActiveStep = useCallback(() => {
    const step = steps[activeStep];
    return (
      <step.component {...rest} onSubmitEnd={onSubmitEnd} variant="standard" />
    );
  }, [activeStep, onSubmitEnd, rest, steps]);

  const handleNextForm = useCallback(() => {
    // Trigger a submit to active form
    // This is needed because our modal action buttons are oustide the form
    contactsContext.currentForm && contactsContext.currentForm.submit();
  }, [contactsContext.currentForm]);

  const handleBackForm = useCallback(() => {
    if (activeStep > 0) {
      setContactsContext({ ...contactsContext, currentForm: undefined });
      setActiveStep(activeStep - 1);
    }
  }, [activeStep, contactsContext]);
  return (
    <Dialog
      open={showDialog}
      aria-label="Manage Contacts"
      maxWidth={false}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return null;
        if (reason === 'escapeKeyDown') return handleCloseClick();
      }}
      classes={{
        paperWidthFalse: classes.paperWidthFalse // class name, e.g. `classes-nesting-root-x`
      }}
    >
      <DialogTitleWithCloseButton onClose={handleCloseClick}>
        {props.fundId
          ? 'Add Existing Investor to Fund'
          : 'Add Existing Fund to Investor'}
      </DialogTitleWithCloseButton>
      <DialogContent className={classes.dialogContent}>
        <>
          <div className={classes.stepperContainer}>
            <Stepper
              activeStep={activeStep}
              className={classes.stepper}
              connector={ids.length === 0 ? <></> : <StepConnector />}
            >
              {steps.map(step => (
                <Step key={step.name}>
                  {step.shouldDisplay && <StepLabel>{step.label}</StepLabel>}
                </Step>
              ))}
            </Stepper>
          </div>
          <div className={classes.stepContainer}>
            <ManageContactContext.Provider value={providerContactsContext}>
              {getActiveStep()}
            </ManageContactContext.Provider>
          </div>
          <Confirm
            isOpen={confirmDialogIsOpen}
            title="Close Window?"
            content={
              <div>
                <p style={{ margin: 0 }}>
                  You are about to quit the create operation, and return to
                  {props.fundId ? ' Fund' : ' Investor'} page.
                </p>
                <br />
                Your progress on this step will not be saved.
              </div>
            }
            onConfirm={handleClose}
            onClose={handleConfirmDialogClose}
            confirm="ra.action.close"
          />
        </>
      </DialogContent>
      <DialogActions style={{ borderTop: `1px solid rgba(0, 0, 0, 0.12)` }}>
        <div className={classes.leftWrapper}>
          {hasBack && (
            <Button
              onClick={handleBackForm}
              variant="outlined"
              color="primary"
              className={classes.backButton}
            >
              Back
            </Button>
          )}
        </div>
        <Button
          onClick={handleClose}
          color="primary"
          variant="outlined"
          className={classes.button}
        >
          Cancel
        </Button>
        <Button
          onClick={handleNextForm}
          color="primary"
          variant="contained"
          className={classes.button}
          loading={props.loading}
        >
          {hasNext ? 'Next' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(styles)(FundInvestorStepper);
